import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BookmarkRecord } from 'fl-bookmarks';
import { ContentAuthorizationData } from 'fl-content-authorization';
import { Player } from 'fl-player-interface';
import { ResourceVm } from 'qp-ui-interface';
import { Observable } from 'rxjs';
import { PageType } from 'src/app/analytics/newrelic/enums/PageType';
import { v4 as uuidv4 } from 'uuid';
import {
  hasEpisodeTypes,
  isEpisodeType,
  PlayerContainerType,
} from '../common/const/common.const';
import {
  ResourceActionState,
  RouteStrategy,
} from '../common/interfaces/common.interface';
import { ContentType } from '../enums/contentType';
import { SubscriptionAction, SubscriptionStatus } from '../enums/subscription';
import { GetPlatformPlayerModel } from '../interfaces/player.interface';
import { DetailAhaGoldDialogComponent } from '../shared-components/aha-upgrade-prompts/detail/detail-aha-gold-dialog/detail-aha-gold-dialog.component';
import { DetailAhaGoldPromptComponent } from '../shared-components/aha-upgrade-prompts/detail/detail-aha-gold-prompt/detail-aha-gold-prompt.component';
import { NoActiveSubscriptionComponent } from '../shared-components/subscription-alerts/no-active-subscription/no-active-subscription.component';
import { RentToWatchComponent } from '../shared-components/tvod/rent-to-watch/rent-to-watch.component';
import { BrowserDetector, getBrowserDetector } from '../utils/browserUtils';
import { AppConfig } from '../utils/constants/app-config.constant';
import { getAuthHeaders, getBaseRequestData } from '../utils/evergentUtils';
import {
  getAutoPreviewPlayer,
  getPlatformPlayer,
} from '../utils/PlaybackUtils';
import { AuthService } from './auth/auth.service';
import { ConfigService } from './config.service';
import { DialogService } from './dialog.service';
import { ContentEntitlementService } from './evergent/entitlement/content-entitlement.service';
import { EntitlementService } from './evergent/entitlement/entitlement.service';
import { RecordPlayTimeRequestMessage } from './evergent/entitlement/model/entitlement/record-play-time.request.message';
import { RecordPlayTimeRequestModel } from './evergent/entitlement/model/entitlement/record-play-time.request.model';
import { RecordPlayTimeResponseMessage } from './evergent/entitlement/model/entitlement/record-play-time.response.message';
import { vodDetails } from './evergent/entitlement/model/entitlement/vod-catalog-response.model';
import { VODDetailsMessage } from './evergent/entitlement/model/entitlement/vod-details.message';
import { LocalStorageService } from './storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class PlaybackService {
  browserDetector: BrowserDetector;
  private playbackContentId: string;
  private playbackSessionId: string;
  private startTimePlayback = 0;
  thumbnailSupported = false;
  showThumbnails = true;
  channelNamespace: string;
  ovatToken?: string;
  playbacksessionID: string;
  programsessionID: string;
  private creatingPlayer = false;

  constructor(
    private readonly http: HttpClient,
    private configService: ConfigService,
    private readonly authService: AuthService,
    private readonly entitlementService: EntitlementService,
    private readonly contentEntitlementService: ContentEntitlementService,
    private readonly translate: TranslateService,
    private readonly dialogService: DialogService,
    private readonly localStorage: LocalStorageService
  ) {
    this.browserDetector = getBrowserDetector();
    this.channelNamespace = 'urn:x-cast:com.quickplay.platform.player.cast';
  }

  getTranslatedText(key: string) {
    return this.translate.instant(key);
  }

  getTranslatedTextWithParams(key: string, params: any[]) {
    let translatedText = this.translate.instant(key);
    for (let i = 0; i < params.length; i++) {
      translatedText = translatedText.replace(`{${i}}`, String(params[i]));
    }
    return translatedText;
  }

  set startTime(startTime: number) {
    this.startTimePlayback = startTime;
  }

  get startTime(): number {
    return this.startTimePlayback;
  }

  resetStartTime(): void {
    this.startTimePlayback = Date.now();
  }

  get bookmarkService() {
    return this.authService.bookmarkService;
  }

  get favoriteService() {
    return this.authService.favoriteService;
  }

  get playingContentId() {
    return this.playbackContentId;
  }

  thumbnailsValidation(): boolean {
    const thumbnailsConfigValue =
      this.configService?.applicationConfig?.enableKeyframeV2 === 'true' ||
      true;
    this.showThumbnails = thumbnailsConfigValue;
    return this.showThumbnails;
  }

  get playbackSessionID(): string {
    return this.playbackSessionId;
  }

  set playbackSessionID(newVal: string) {
    this.playbackSessionId = newVal;
  }

  public async getSimplePlayer(resource: ResourceVm, video: HTMLVideoElement) {
    return await getAutoPreviewPlayer(resource.apUrl?.dash, video);
  }

  async createPlayer(
    contentAuthorizationData: ContentAuthorizationData,
    checkforThumbnails: boolean,
    isLive: boolean,
    seekPos: number = 0,
    videoElement: HTMLVideoElement,
    videoContainer: HTMLElement,
    resource: ResourceVm
  ): Promise<Player> {
    this.creatingPlayer = true;
    try {
      const certificate = await this.authService.fetchWVCertificate();
      this.thumbnailSupported = checkforThumbnails
        ? this.thumbnailsValidation()
        : false;
      const getplatformPlayerModel: GetPlatformPlayerModel = {
        seekPos,
        applicationConfig: this.configService.applicationConfig,
        isLive,
        contentAuthorizationData,
        video: videoElement,
        videoContainer: videoContainer,
        certificate,
        showThumbnails: this.thumbnailSupported,
        resource,
      };
      let platformPlayer = await getPlatformPlayer(
        getplatformPlayerModel,
        this.authService.isLoggedIn()
      );
      this.playbacksessionID = uuidv4();
      this.programsessionID = uuidv4();
      return platformPlayer;
    } catch (error) {
      console.error('[PL] Error creating player: ', error);
      return Promise.reject();
    }
  }

  getResourceActionState(
    activeResource: ResourceVm,
    resource?: ResourceVm,
    bookmark?: BookmarkRecord
  ): ResourceActionState {
    const subscriptionStatus =
      this.contentEntitlementService.canPlayVideo(activeResource);
    const canPlayVideo =
      this.contentEntitlementService.isContentPlayable(subscriptionStatus);
    const hasBookmark = isEpisodeType.includes(
      activeResource.contentType as ContentType
    )
      ? bookmark?.episodeId === activeResource.id
      : bookmark?.contentId === activeResource.id;
    const isLoggedIn = this.authService.isLoggedIn();
    const isSVODContent =
      this.contentEntitlementService.isSVODContent(activeResource);
    const hasTrailer = Boolean(
      resource?.trailers?.length > 0 || activeResource.trailers?.length > 0
    );
    const tvodPurchasedList =
      this.contentEntitlementService.getTvodPurchasedList(activeResource);
    const tvodItem =
      (tvodPurchasedList?.length && tvodPurchasedList?.[0]) || null;
    const matchedCatalog = this.entitlementService.tvodCatalog?.find(
      (c) =>
        c.skuORQuickCode === activeResource?.tvodPurchaseConfiguration?.sku?.[0]
    );
    // SVOD content needing subscription
    if (!canPlayVideo && isSVODContent) {
      return {
        showPlayButton: false,
        showTrailerButton: hasTrailer,
        showSubscriptionActionButton:
          subscriptionStatus === SubscriptionStatus.NEEDS_UPGRADE
            ? SubscriptionAction.UPGRADE
            : SubscriptionAction.SUBSCRIBE,
        showTvodButton: false,
        showStartOverButton: false,
        showAddToListButton: isLoggedIn,
        subscriptionActionText:
          subscriptionStatus === SubscriptionStatus.NEEDS_UPGRADE
            ? this.getTranslatedText('upgrade').toUpperCase()
            : this.getTranslatedText('menu_subscribe_now_btn_label'),
      };
    } else if (
      activeResource.isTvod &&
      subscriptionStatus == SubscriptionStatus.TVOD_NEEDS_SUBSCRIPTION
    ) {
      const tvodPrice = matchedCatalog?.retailPrice || 0;
      const currencySymbol = matchedCatalog?.currencySymbol || '';
      const tvodHint = this.getTvodHintText(
        activeResource,
        subscriptionStatus,
        bookmark,
        matchedCatalog,
        tvodItem
      );
      return {
        showPlayButton: false,
        showTrailerButton: hasTrailer,
        showTvodButton: true,
        showStartOverButton: false,
        showAddToListButton: isLoggedIn,
        tvodHintText: tvodHint,
        tvodButtonText: `${this.getTranslatedText(
          'rent_for'
        )} ${currencySymbol}${tvodPrice}`,
        catalogItem: matchedCatalog,
      };
    } else if (
      activeResource.isTvod &&
      subscriptionStatus == SubscriptionStatus.TVOD_SUBSCRIBED
    ) {
      const tvodHint = this.getTvodHintText(
        activeResource,
        subscriptionStatus,
        bookmark,
        matchedCatalog,
        tvodItem
      );
      return {
        showPlayButton: true,
        showTrailerButton: hasTrailer && !hasBookmark,
        showTvodButton: false,
        showStartOverButton: hasBookmark,
        showAddToListButton: isLoggedIn,
        playButtonText: this.getPlayButtonText(
          activeResource,
          subscriptionStatus,
          bookmark
        ),
        tvodHintText: tvodHint,
      };
    } else {
      return {
        showPlayButton: true,
        showTrailerButton: !hasBookmark && hasTrailer,
        showStartOverButton: hasBookmark,
        showAddToListButton: isLoggedIn,
        playButtonText: this.getPlayButtonText(
          activeResource,
          subscriptionStatus,
          bookmark
        ),
      };
    }
  }

  showPopup(
    resource: ResourceVm,
    routeStrategy?: RouteStrategy,
    pageID?: PageType,
    playerContainerType?: PlayerContainerType,
    customData: any = {}
  ) {
    const action = this.getResourceActionState(resource);
    if (!action.showPlayButton) {
      if (action.showTvodButton) {
        this.dialogService.openDialog(RentToWatchComponent, {
          data: {
            resource,
            isTvod: true,
            redirectFrom: playerContainerType ? 'player' : undefined,
            pageID,
          },
        });
        return true;
      } else if (action.showSubscriptionActionButton) {
        if (
          action.showSubscriptionActionButton === SubscriptionAction.UPGRADE
        ) {
          if (playerContainerType) {
            this.dialogService.openDialog(DetailAhaGoldDialogComponent, {
              data: {
                resource,
                buttonText: 'go_back',
                redirectFrom: playerContainerType,
                ...customData,
              },
            });
          } else {
            this.dialogService.openDialog(DetailAhaGoldPromptComponent, {
              data: {
                resource,
                buttonText: 'go_back',
              },
            });
          }
          return true;
        } else {
          this.dialogService.openDialog(NoActiveSubscriptionComponent, {
            data: {
              routeStrategy: pageID === PageType.Player ? null : routeStrategy,
              pageID,
            },
          });
          return true;
        }
      }
    }
    return false;
  }

  private getTvodHintText(
    catalogItem: ResourceVm,
    subscriptionStatus: SubscriptionStatus,
    bookmark: BookmarkRecord,
    vodCatalog?: vodDetails,
    tvodItem?: VODDetailsMessage
  ) {
    // const vodCatalog = vodCatalogs?.find(
    //   (c) => c.skuORQuickCode === catalogItem.tvodPurchaseConfiguration.sku?.[0]
    // );
    if (subscriptionStatus == SubscriptionStatus.TVOD_NEEDS_SUBSCRIPTION) {
      const rentalDays = parseInt(vodCatalog?.rentalPeriod) / 24;
      const viewingHours = parseInt(vodCatalog?.viewingPeriod);

      // Return null if either value is NaN
      if (isNaN(rentalDays) || isNaN(viewingHours)) {
        return null;
      }

      return this.getTranslatedTextWithParams('rental_purchase_validity', [
        rentalDays,
        viewingHours,
      ]);
    } else if (
      subscriptionStatus == SubscriptionStatus.TVOD_SUBSCRIBED &&
      !!bookmark
    ) {
      // additional logics
      if (tvodItem.startTime) {
        const viewingPeriodHours = parseInt(vodCatalog?.viewingPeriod);
        if (isNaN(viewingPeriodHours)) {
          return null;
        }
        const remainingHours = this.calculateRemainingTime(
          tvodItem.startTime,
          viewingPeriodHours
        );
        if (remainingHours === null) {
          return null;
        }
        return this.getTranslatedTextWithParams('rental_ends_desc', [
          remainingHours,
        ]);
      } else {
        const validityEndDate = parseInt(tvodItem.validityEndDate);
        if (isNaN(validityEndDate)) {
          return null;
        }
        const expiryDate = this.formatExpiryDate(validityEndDate);
        if (expiryDate === null) {
          return null;
        }
        return `${this.getTranslatedText('rental_ends')} ${expiryDate}`;
      }
    } else {
      const validityEndDate = parseInt(tvodItem.validityEndDate);
      if (isNaN(validityEndDate)) {
        return null;
      }
      const expiryDate = this.formatExpiryDate(validityEndDate);
      if (expiryDate === null) {
        return null;
      }
      return `${this.getTranslatedText('rental_ends')} ${expiryDate}`;
    }
  }

  private calculateRemainingTime(
    startTime: number,
    viewingPeriodHours: number
  ) {
    // Additional safety check for NaN values
    if (isNaN(startTime) || isNaN(viewingPeriodHours)) {
      return null;
    }

    const currentTime = Date.now();
    const elapsedMillis = currentTime - startTime;

    const elapsedHours = Math.floor(elapsedMillis / (1000 * 60 * 60));
    const remainingHours = Math.max(viewingPeriodHours - elapsedHours, 0);

    if (remainingHours > 1) {
      return `${remainingHours} hrs`;
    } else if (remainingHours === 1) {
      return '1 hr';
    } else {
      const remainingMinutes = Math.floor(
        viewingPeriodHours * 60 - elapsedMillis / (1000 * 60)
      );
      return `${remainingMinutes} mins`;
    }
  }

  private formatExpiryDate(timestamp: number) {
    // Check if timestamp is NaN or invalid
    if (isNaN(timestamp)) {
      return null;
    }

    const dateFormat = new Date(timestamp);
    return dateFormat.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private getPlayButtonText(
    catalogItem: ResourceVm,
    subscriptionStatus: SubscriptionStatus,
    bookmark: BookmarkRecord
  ) {
    if (subscriptionStatus == SubscriptionStatus.FREE_PREVIEW) {
      return this.getTranslatedText('content_detail.free_preview');
    } else if (catalogItem.isTvod) {
      if (bookmark) {
        // "content_detail.episode_ordinal_lbl": "Episode {0}",
        if (hasEpisodeTypes.includes(catalogItem.contentType as ContentType)) {
          return `${this.getTranslatedText('player.season')} ${
            catalogItem.seasonNumber
          }: ${this.getTranslatedTextWithParams(
            'content_detail.episode_ordinal_lbl',
            [catalogItem.episodeNumber]
          )}`;
        } else {
          return this.getTranslatedText('my_content.continue_watching');
        }
      } else {
        if (hasEpisodeTypes.includes(catalogItem.contentType as ContentType)) {
          return `${this.getTranslatedText('player.season')} ${
            catalogItem.seasonNumber
          }: ${this.getTranslatedTextWithParams(
            'content_detail.episode_ordinal_lbl',
            [catalogItem.episodeNumber]
          )}`;
        } else {
          return this.getTranslatedText('content_detail.start_watching');
        }
      }
    } else if (
      hasEpisodeTypes.includes(catalogItem.contentType as ContentType)
    ) {
      if (bookmark) {
        return `${this.getTranslatedText('global.btn_continue')} S${
          catalogItem.seasonNumber
        }: E${catalogItem.episodeNumber}`;
      } else {
        return `${this.getTranslatedText('player.season')} ${
          catalogItem.seasonNumber
        }: ${this.getTranslatedTextWithParams(
          'content_detail.episode_ordinal_lbl',
          [catalogItem.episodeNumber]
        )}`;
      }
    } else {
      if (bookmark) {
        return this.getTranslatedText('my_content.continue_watching');
      } else {
        return this.getTranslatedText('content_detail.start_watching');
      }
    }
  }

  recordTvod(assetId: string): Observable<RecordPlayTimeResponseMessage> {
    const recordPlayTimeRequestMessage = this.recordPlayTimeRequest(assetId);
    const appConfig: AppConfig = this.configService.applicationConfig;
    const url = `${appConfig.UmsURL}/aha/recordPlayTime`;

    return this.http.post<RecordPlayTimeResponseMessage>(
      url,
      recordPlayTimeRequestMessage,
      getAuthHeaders(this.localStorage)
    );
  }
  private recordPlayTimeRequest(assetId: string): RecordPlayTimeRequestMessage {
    const requestMessage = new RecordPlayTimeRequestMessage();
    let requestContent: RecordPlayTimeRequestModel =
      new RecordPlayTimeRequestModel();
    requestContent.assetId = assetId;
    requestContent = {
      ...requestContent,
      ...getBaseRequestData(this.configService.applicationConfig),
    };
    requestMessage.RecordPlayTimeRequestMessage = requestContent;
    return requestMessage;
  }
}
